/**
 * The Git tab (docs/DESIGN-GIT-TAB.md): every service of the project is its own git repo; one compact row each with its
 * branch (a menu: switch, New branch…), what changed, ahead/behind its upstream and the last commit; Commit, Pull and
 * Push (a split menu to pick a remote, or All remotes, when there are several), and ⋯ for Tag…, Tags… (local and remote,
 * Pull tags, Check out a tag detached), Remotes…, Fetch (branches and tags, one remote or all), Open on <host> and Copy
 * clone URL. The branch menu also lists the remote branches (a checkout makes the tracking branch). On top: Fetch all, Pull all, Push all (each service with its default remote) and Clone from Flow
 * (Flow is the git server of every service: a service with no remote is "Hosted by Flow"; no `flow.cloneBase`: the
 * server does not offer it, no button). A 409 on push: pull, then push again. Read again when the page gets the focus
 * back, and live on the project's tree events (a file saved, a terminal command, `git:pushed` from a clone), a little
 * after the last one.
 *
 * Light on purpose: no diff, no graph; details are on the git server (Open on <host>) or in History. `canWrite` false
 * (a viewer): the overview, Remotes to read and the clone URLs, no write action. A repo flagged Read-only
 * (utils/repoFlags.js) shows its badge and keeps Fetch and Pull only: Commit, Push, New branch and Tag are disabled
 * with the reason (vfs-service refuses them anyway). Data: services/GitService.js.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dropdown, Space, Tooltip } from 'antd';
import { ArrowsClockwise, CaretDown, DotsThree, GitBranch } from '@phosphor-icons/react';
import config from '../../config';
import * as git from '../../services/GitService';
import { connectProjectTreeSyncRaw } from '../../utils/vfsSync';
import {
  BranchModal, CommitModal, GitErrorText, RemotesModal, TagModal, TagsModal,
} from './GitDialogs';
import CloneFromFlow from './CloneFromFlow';
import {
  bulkSummary, changedCount, defaultRemote, flowCloneUrl, lastCommitText, remoteBranchesOf, remoteHost, remoteWebUrl, remotesOf,
} from './gitModel';
import useRepoFlags from '../../hooks/useRepoFlags';
import RepoFlagBadges from '../repoFlags/RepoFlagBadges';
import './Git.css';

export const ALL_REMOTES = '__all__';

async function copy(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}

/** A push refused because the remote moved on (409): say what to do. */
export function withPushHint(error) {
  if (!error || error.status !== 409 || error.code === 'tag_exists') { return error; }
  const hint = 'Pull, then push again.';
  return String(error.message || '').includes(hint) ? error : Object.assign(Object.create(Object.getPrototypeOf(error)), error, { message: `${error.message || 'The remote has newer commits.'} ${hint}` });
}

/** What a push or pull answered, in words; `qualityWarning` (the release gate fails) is kept as a note. */
export function resultText(verb, results) {
  const failed = results.filter((r) => !r.ok);
  const ok = results.filter((r) => r.ok);
  const warning = ok.map((r) => r.result?.qualityWarning?.message || (typeof r.result?.qualityWarning === 'string' ? r.result.qualityWarning : null)).find(Boolean);
  if (failed.length) { return { kind: 'error', error: withPushHint(failed[0].error), text: ok.length ? `${verb} ${ok.map((r) => r.remote).join(', ')}.` : null }; }
  return { kind: 'ok', text: `${verb} ${ok.map((r) => r.remote).join(', ')}.${warning ? ` ${warning}` : ''}` };
}

function SplitRemoteButton({ label, verb, repo, disabled, onRun, title = undefined }) {
  const remotes = remotesOf(repo);
  const main = defaultRemote(repo);
  if (remotes.length <= 1) {
    return <Button data-testid={`git-${verb}-${repo.service}`} disabled={disabled} onClick={() => onRun([main])} title={title}>{label}</Button>;
  }
  const items = [
    ...remotes.map((r) => ({ key: r.name, label: `${label} ${r.name}${r.name === main ? ' (default)' : ''}` })),
    { type: 'divider' },
    { key: ALL_REMOTES, label: 'All remotes' },
  ];
  return (
    <Space.Compact>
      <Button data-testid={`git-${verb}-${repo.service}`} disabled={disabled} onClick={() => onRun([main])} title={title}>{label}</Button>
      <Dropdown
        disabled={disabled}
        menu={{ items, onClick: ({ key }) => onRun(key === ALL_REMOTES ? remotes.map((r) => r.name) : [key]) }}
        trigger={['click']}
      >
        <Button aria-label={`${label} to…`} data-testid={`git-${verb}-pick-${repo.service}`} icon={<CaretDown aria-hidden size={12} />} />
      </Dropdown>
    </Space.Compact>
  );
}

SplitRemoteButton.propTypes = {
  disabled: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onRun: PropTypes.func.isRequired,
  repo: PropTypes.object.isRequired,
  title: PropTypes.string,
  verb: PropTypes.string.isRequired,
};

function ServiceRow({ repo, canWrite, busy, notice, cloneBase, onAction, onDialog, flags = null, focused = false }) {
  // A read-only repo: why Commit, Push, New branch and Tag are off.
  const locked = flags?.readOnly ? `${flags.repo} is read-only in this project: only Fetch and Pull.` : null;
  const remotes = remotesOf(repo);
  const count = changedCount(repo);
  const last = lastCommitText(repo);
  const branches = Array.isArray(repo.branches) && repo.branches.length ? repo.branches : [repo.branch].filter(Boolean);
  const cloneUrl = flowCloneUrl(cloneBase, repo.service);
  const branchChip = (
    <span className="git-chip is-branch" data-testid={`git-branch-${repo.service}`}>
      <GitBranch aria-hidden size={13} />
      {repo.branch || 'detached'}
      {canWrite && <CaretDown aria-hidden size={10} />}
    </span>
  );
  const remoteBranches = remoteBranchesOf(repo);
  const branchMenu = {
    items: [
      ...branches.map((b) => ({ key: `b:${b}`, label: b, disabled: b === repo.branch })),
      ...(remoteBranches.length ? [
        { type: 'divider' },
        {
          type: 'group',
          label: 'On the remote',
          children: remoteBranches.map((b) => ({ key: `r:${b.ref}`, label: <span data-testid={`git-remote-branch-${b.ref}`}>{b.ref} <span className="git-muted">remote</span></span> })),
        },
      ] : []),
      { type: 'divider' },
      { key: 'new', label: locked ? <span title={locked}>New branch…</span> : 'New branch…', disabled: Boolean(locked) },
      ...(remotes.length ? [{ key: 'fetch', label: 'Fetch branches and tags' }] : []),
    ],
    onClick: ({ key }) => {
      if (key === 'new') { onDialog('branch'); return; }
      if (key === 'fetch') { onAction('fetch', { remotes: remotes.map((r) => r.name) }); return; }
      if (key.startsWith('r:')) { onAction('checkout', { branch: key.slice(2), remote: true }); return; }
      onAction('checkout', { branch: key.slice(2) });
    },
  };
  const moreItems = [
    ...(canWrite ? [{ key: 'tag', label: locked ? <span title={locked}>Tag…</span> : 'Tag…', disabled: Boolean(locked) }] : []),
    { key: 'tags', label: 'Tags…' },
    { key: 'remotes', label: 'Remotes…' },
    ...remotes.filter((r) => remoteWebUrl(r)).map((r) => ({ key: `open:${r.name}`, label: `Open on ${remoteHost(r) || r.name}${remotes.length > 1 ? ` (${r.name})` : ''}` })),
    ...(canWrite && remotes.length === 1 ? [{ key: 'fetch', label: 'Fetch (branches and tags)' }] : []),
    ...(canWrite && remotes.length > 1 ? [
      ...remotes.map((r) => ({ key: `fetch:${r.name}`, label: `Fetch ${r.name}` })),
      { key: 'fetch', label: 'Fetch all remotes' },
    ] : []),
    ...(cloneUrl ? [{ key: 'copy', label: 'Copy clone URL' }] : []),
  ];
  const onMore = ({ key }) => {
    if (key === 'tag' || key === 'remotes' || key === 'tags') { onDialog(key); return; }
    if (key === 'fetch') { onAction('fetch', { remotes: remotes.map((r) => r.name) }); return; }
    if (key.startsWith('fetch:')) { onAction('fetch', { remotes: [key.slice(6)] }); return; }
    if (key === 'copy') { copy(cloneUrl); return; }
    if (key.startsWith('open:')) {
      const web = remoteWebUrl(remotes.find((r) => r.name === key.slice(5)));
      if (web) { window.open(web, '_blank', 'noopener,noreferrer'); }
    }
  };
  return (
    <li className={`git-service${busy ? ' is-busy' : ''}${focused ? ' is-focused' : ''}`} data-testid={`git-row-${repo.service}`}>
      <div className="git-service-line">
        <span className="git-service-name" title={repo.service}>{repo.service}</span>
        <RepoFlagBadges flags={flags} testId={`git-flags-${repo.service}`} />
        {canWrite
          ? <Dropdown disabled={busy} menu={branchMenu} trigger={['click']}><button aria-label={`Branch of ${repo.service}: ${repo.branch || 'detached'}`} className="git-chip-button" type="button">{branchChip}</button></Dropdown>
          : branchChip}
        {repo.initialized === false && <span className="git-chip is-changed" data-testid={`git-uninit-${repo.service}`}>no repository yet</span>}
        {Number(repo.conflicted) > 0 && <span className="git-chip is-conflict" data-testid={`git-conflicts-${repo.service}`}>{`${repo.conflicted} ${Number(repo.conflicted) === 1 ? 'conflict' : 'conflicts'}`}</span>}
        {count > 0
          ? <span className="git-chip is-changed" data-testid={`git-changed-${repo.service}`}><span aria-hidden className="git-dot" />{`${count} changed`}</span>
          : <span className="git-chip is-clean">clean</span>}
        {remotes.length > 0
          ? (
            <span className="git-sync" data-testid={`git-sync-${repo.service}`} title={repo.upstream ? `Compared with ${repo.upstream}` : 'No upstream yet'}>
              {repo.upstream ? `↑${Number(repo.ahead) || 0} ↓${Number(repo.behind) || 0}` : 'not pushed yet'}
            </span>
          )
          : <span className="git-sync is-flow">Hosted by Flow</span>}
        <span className="git-last" title={repo.lastCommit?.sha || undefined}>{last || 'no commits yet'}</span>
        <span className="git-actions">
          {canWrite && (
            <Button data-testid={`git-commit-${repo.service}`} disabled={busy || count === 0 || Boolean(locked)} onClick={() => onDialog('commit')} title={locked || undefined}>Commit</Button>
          )}
          {canWrite && remotes.length > 0 && (
            <>
              <SplitRemoteButton disabled={busy} label="Pull" onRun={(names) => onAction('pull', { remotes: names })} repo={repo} verb="pull" />
              <SplitRemoteButton disabled={busy || Boolean(locked)} label="Push" onRun={(names) => onAction('push', { remotes: names })} repo={repo} title={locked || undefined} verb="push" />
            </>
          )}
          {canWrite && remotes.length === 0 && (
            <Button data-testid={`git-add-remote-${repo.service}`} disabled={busy} onClick={() => onDialog('remotes')}>Add remote</Button>
          )}
          <Dropdown menu={{ items: moreItems, onClick: onMore }} trigger={['click']}>
            <Button aria-label={`More for ${repo.service}`} data-testid={`git-more-${repo.service}`} icon={<DotsThree aria-hidden size={18} weight="bold" />} type="text" />
          </Dropdown>
        </span>
      </div>
      {notice?.kind === 'ok' && <p className="git-note" role="status">{notice.text}</p>}
      {notice?.kind === 'error' && (
        <>
          {notice.text && <p className="git-note" role="status">{notice.text}</p>}
          <GitErrorText error={notice.error} />
        </>
      )}
    </li>
  );
}

ServiceRow.propTypes = {
  busy: PropTypes.bool,
  focused: PropTypes.bool,
  canWrite: PropTypes.bool.isRequired,
  cloneBase: PropTypes.string,
  // The repo's flags (utils/repoFlags.js), with its key.
  flags: PropTypes.object,
  notice: PropTypes.object,
  onAction: PropTypes.func.isRequired,
  onDialog: PropTypes.func.isRequired,
  repo: PropTypes.object.isRequired,
};

export default function GitTab({ projectId, readOnly = false, focus = null }) {
  const canWrite = !readOnly;
  const [state, setState] = useState({ loading: true, error: null, repos: [], flow: {} });
  // "Open in Git tab" from a service's page (focus.service, "api" or "services/api"): its row in view, marked a moment.
  const wanted = typeof focus?.service === 'string' ? focus.service.replace(/^services\//, '') : '';
  const [focused, setFocused] = useState(null);
  useEffect(() => {
    if (!wanted || !state.repos.some((repo) => repo.service === wanted)) { return undefined; }
    setFocused(wanted);
    document.querySelector(`[data-testid="git-row-${CSS.escape(wanted)}"]`)?.scrollIntoView?.({ block: 'center' });
    const timer = setTimeout(() => setFocused(null), 2500);
    return () => clearTimeout(timer);
  }, [wanted, focus, state.repos]);
  const [busy, setBusy] = useState({});
  const [notices, setNotices] = useState({});
  const [bulk, setBulk] = useState(null);
  const [dialog, setDialog] = useState(null);
  const { serviceFlags } = useRepoFlags(projectId);

  const load = useCallback(async() => {
    try {
      const data = await git.getOverview(projectId);
      setState({ loading: false, error: null, repos: data.repos, flow: data.flow });
    } catch (error) {
      setState((prev) => ({ ...prev, loading: false, error }));
    }
  }, [projectId]);

  useEffect(() => {
    setState({ loading: true, error: null, repos: [], flow: {} });
    setNotices({});
    setBulk(null);
    load();
  }, [load]);

  // Files saved, a terminal command, a push from a clone (`git:pushed`): read again shortly after the last one.
  useEffect(() => {
    if (!config.vfsServiceUrl || !projectId) { return undefined; }
    let timer = null;
    const soon = () => { clearTimeout(timer); timer = setTimeout(load, 800); };
    const disconnect = connectProjectTreeSyncRaw(config.vfsServiceUrl, projectId, {
      onFullResync: (type) => { if (type !== 'tree:init') { soon(); } },
      onPathChanged: soon,
      onPathDeleted: soon,
      onPathMoved: soon,
      onGitEvent: soon,
    });
    return () => { clearTimeout(timer); disconnect(); };
  }, [projectId, load]);

  // A push from a clone (Flow as the git server) changes a repo behind this page: read again when the page is back.
  useEffect(() => {
    const again = () => { if (document.visibilityState !== 'hidden') { load(); } };
    window.addEventListener('focus', again);
    document.addEventListener('visibilitychange', again);
    return () => {
      window.removeEventListener('focus', again);
      document.removeEventListener('visibilitychange', again);
    };
  }, [load]);

  const setBusyFor = (service, on) => setBusy((prev) => ({ ...prev, [service]: on }));
  const notify = (service, notice) => setNotices((prev) => ({ ...prev, [service]: notice }));

  /** Runs one row's action, its notice after, then reads the overview again. */
  const act = async(repo, kind, args = {}) => {
    const { service } = repo;
    setBusyFor(service, true);
    notify(service, null);
    try {
      if (kind === 'push' || kind === 'pull') {
        const run = kind === 'push' ? git.pushAll : git.pullAll;
        const results = await run(projectId, { service, remotes: args.remotes || [defaultRemote(repo)], ...(args.tag ? { tag: args.tag } : {}) });
        notify(service, resultText(kind === 'push' ? 'Pushed to' : 'Pulled from', results));
      } else if (kind === 'fetch') {
        const names = args.remotes?.length ? args.remotes : [defaultRemote(repo)];
        const results = await git.fetchAll(projectId, { service, remotes: names, tags: true });
        notify(service, resultText('Fetched branches and tags of', results));
      } else if (kind === 'checkout') {
        const answer = await git.checkout(projectId, args.tag ? { service, tag: args.tag } : { service, branch: args.branch });
        let text = `Switched to ${args.branch}.`;
        if (args.tag) { text = `Detached at tag ${args.tag}: commits made here are on no branch. Pick a branch to leave.`; }
        else if (args.remote) { text = `Switched to ${answer?.branch || args.branch}, tracking ${args.branch}.`; }
        notify(service, { kind: 'ok', text });
      }
    } catch (error) {
      notify(service, { kind: 'error', error });
    } finally {
      setBusyFor(service, false);
    }
    await load();
  };

  /** A dialog's action: it throws to keep the dialog open with the error; the row's notice and a reload after. */
  const fromDialog = (repo) => ({
    commit: async(message) => {
      const answer = await git.commit(projectId, { service: repo.service, message });
      const first = Array.isArray(answer?.shas) ? answer.shas[0] : null;
      const sha = first ? ` ${String(typeof first === 'object' ? first.sha || '' : first).slice(0, 7)}` : '';
      notify(repo.service, { kind: 'ok', text: `Committed${sha}.` });
      load();
    },
    tag: async({ name, message, push }) => {
      await git.createTag(projectId, { service: repo.service, name, message });
      notify(repo.service, { kind: 'ok', text: `Tagged ${name}.` });
      if (push) { act(repo, 'push', { remotes: [defaultRemote(repo)], tag: name }); } else { load(); }
    },
    branch: async(name) => {
      await git.createBranch(projectId, { service: repo.service, name });
      notify(repo.service, { kind: 'ok', text: `On the new branch ${name}.` });
      load();
    },
  });

  const bulkRun = async(kind) => {
    // Push all leaves out the read-only repos (they keep Fetch and Pull).
    const targets = state.repos.filter((repo) => remotesOf(repo).length > 0 && !(kind === 'push' && serviceFlags(repo.service)?.readOnly));
    if (!targets.length) { return; }
    setBulk({ running: kind, text: null });
    const results = [];
    for (const repo of targets) {
      setBusyFor(repo.service, true);
      try {
        const remote = defaultRemote(repo);
        // eslint-disable-next-line no-await-in-loop -- one service at a time, in the order shown
        if (kind === 'fetch') {
          const each = await git.fetchAll(projectId, { service: repo.service, remotes: remotesOf(repo).map((r) => r.name), tags: true });
          const failed = each.find((r) => !r.ok);
          if (failed) { throw failed.error; }
        } else {
          await (kind === 'push' ? git.push(projectId, { service: repo.service, remote }) : git.pull(projectId, { service: repo.service, remote }));
        }
        results.push({ label: repo.service, ok: true });
      } catch (error) {
        const shown = kind === 'push' ? withPushHint(error) : error;
        results.push({ label: repo.service, ok: false, error: shown });
        notify(repo.service, { kind: 'error', error: shown });
      } finally {
        setBusyFor(repo.service, false);
      }
    }
    const verb = { push: 'Pushed', pull: 'Pulled', fetch: 'Fetched' }[kind];
    setBulk({ running: null, text: bulkSummary(verb, results) });
    await load();
  };

  const withRemote = state.repos.some((repo) => remotesOf(repo).length > 0);
  const anyBusy = Boolean(bulk?.running) || Object.values(busy).some(Boolean);
  const current = dialog ? state.repos.find((repo) => repo.service === dialog.service) : null;
  const closeDialog = () => setDialog(null);

  return (
    <div className="git-panel" data-testid="git-tab">
      {/* The tab's title ("Project / Build / Git") is the workspace's (WorkspaceView); the line under it stays. */}
      <header className="git-head">
        <p className="git-muted">One repository per service. Details on your git server, or in History.</p>
        <div className="git-head-actions">
          <Tooltip title="Refresh">
            <Button aria-label="Refresh" disabled={state.loading} icon={<ArrowsClockwise aria-hidden size={16} />} onClick={load} type="text" />
          </Tooltip>
          {canWrite && withRemote && (
            <>
              <Button data-testid="git-fetch-all" disabled={anyBusy} loading={bulk?.running === 'fetch'} onClick={() => bulkRun('fetch')}>Fetch all</Button>
              <Button data-testid="git-pull-all" disabled={anyBusy} loading={bulk?.running === 'pull'} onClick={() => bulkRun('pull')}>Pull all</Button>
              <Button data-testid="git-push-all" disabled={anyBusy} loading={bulk?.running === 'push'} onClick={() => bulkRun('push')}>Push all</Button>
            </>
          )}
          {state.flow?.cloneBase && <CloneFromFlow canWrite={canWrite} cloneBase={state.flow?.cloneBase || null} projectId={projectId} services={state.repos.map((repo) => repo.service)} />}
        </div>
      </header>
      {readOnly && <p className="git-banner is-info">You can look: committing, pushing and pulling are for the project&apos;s owner and editors.</p>}
      {bulk?.text && <p className="git-banner is-info" data-testid="git-bulk-result" role="status">{bulk.text}</p>}
      {state.loading && <p className="git-muted" role="status">Reading the repositories…</p>}
      {!state.loading && state.error && (
        <div className="git-state is-bad">
          <GitErrorText error={state.error} />
          <div><Button onClick={load}>Try again</Button></div>
        </div>
      )}
      {!state.loading && !state.error && state.repos.length === 0 && (
        <p className="git-state">No services yet: each service you add is a git repository here.</p>
      )}
      {state.repos.length > 0 && (
        <ul aria-label="Services" className="git-services">
          {state.repos.map((repo) => (
            <ServiceRow
              busy={Boolean(busy[repo.service])}
              canWrite={canWrite}
              cloneBase={state.flow?.cloneBase || null}
              flags={serviceFlags(repo.service)}
              focused={focused === repo.service}
              key={repo.service}
              notice={notices[repo.service] || null}
              onAction={(kind, args) => act(repo, kind, args)}
              onDialog={(kind) => setDialog({ kind, service: repo.service })}
              repo={repo}
            />
          ))}
        </ul>
      )}
      {current && dialog.kind === 'commit' && canWrite && !serviceFlags(current.service)?.readOnly && <CommitModal onCancel={closeDialog} onCommit={fromDialog(current).commit} repo={current} />}
      {current && dialog.kind === 'tag' && canWrite && <TagModal onCancel={closeDialog} onTag={fromDialog(current).tag} repo={current} />}
      {current && dialog.kind === 'branch' && canWrite && <BranchModal onCancel={closeDialog} onCreate={fromDialog(current).branch} repo={current} />}
      {current && dialog.kind === 'tags' && (
        <TagsModal
          canWrite={canWrite}
          onCancel={closeDialog}
          onChanged={load}
          onCheckout={(tag) => { closeDialog(); act(current, 'checkout', { tag }); }}
          projectId={projectId}
          repo={current}
        />
      )}
      {current && dialog.kind === 'remotes' && (
        <RemotesModal canWrite={canWrite} cloneBase={state.flow?.cloneBase || null} onCancel={closeDialog} onChanged={load} projectId={projectId} repo={current} />
      )}
    </div>
  );
}

GitTab.propTypes = {
  // `{ service }`: the service to bring into view (a service page's "Open in Git tab").
  focus: PropTypes.object,
  projectId: PropTypes.string.isRequired,
  // A viewer: the overview only, no write action.
  readOnly: PropTypes.bool,
};
